import { ThumbsDown, ThumbsUp } from '@untitled-ui/icons-react';
import type { FC } from 'react';
import { useCallback, useState } from 'react';

import { AIFeedbackModal } from '@/components/ai-feedback/AIFeedbackModal';

import styles from './style.module.scss';
import type { ChatMessage } from './useChatStore';
import { useChatStore } from './useChatStore';

const positiveFeedbackOptions = [
  'The response was helpful',
  'The information was accurate',
  'It understood my question well',
];

const negativeFeedbackOptions = [
  'The response was not relevant',
  'The information was inaccurate',
  "It didn't understand my question",
  'The response was too vague',
];

interface MessageFeedbackProps {
  message: ChatMessage;
  sessionId: string;
  previousUserMessage?: string;
}

export const MessageFeedback: FC<MessageFeedbackProps> = ({
  message,
  sessionId,
  previousUserMessage,
}) => {
  const [feedbackModalVisible, setFeedbackModalVisible] = useState(false);
  const [feedbackIsPositive, setFeedbackIsPositive] = useState(false);
  const { setMessageFeedback } = useChatStore();

  const responseId = message.metadata?.responseId;
  const traceId = message.metadata?.traceId;
  const spanId = message.metadata?.spanId;
  const ddApp = message.metadata?.ddApp;

  const hasFeedback = !!message.feedbackGiven;

  const handleFeedbackClick = (isPositive: boolean) => () => {
    if (hasFeedback) {
      return;
    }

    setFeedbackIsPositive(isPositive);
    setFeedbackModalVisible(true);
  };

  const handleModalDismiss = useCallback(
    (feedbackSubmitted: boolean) => {
      if (feedbackSubmitted) {
        setMessageFeedback(
          message.id,
          feedbackIsPositive ? 'thumbs_up' : 'thumbs_down'
        );
      }
      setFeedbackModalVisible(false);
    },
    [message.id, feedbackIsPositive, setMessageFeedback]
  );

  if (!responseId || !traceId) {
    return null;
  }

  return (
    <>
      {feedbackModalVisible && (
        <AIFeedbackModal
          onDismiss={handleModalDismiss}
          positiveFeedbackOptions={positiveFeedbackOptions}
          negativeFeedbackOptions={negativeFeedbackOptions}
          isPositiveFeedback={feedbackIsPositive}
          spanId={spanId ?? ''}
          traceId={traceId}
          app={ddApp ?? ''}
          sessionId={sessionId}
          responseId={responseId}
          userQuery={previousUserMessage}
          aiResponse={message.content}
        />
      )}
      <div className={styles.feedbackButtons}>
        <button
          onClick={handleFeedbackClick(true)}
          className={`${styles.feedbackButton} ${
            message.feedbackGiven === 'thumbs_up' ? styles.feedbackActive : ''
          }`}
          disabled={hasFeedback}
          title={'Give positive feedback'}
          aria-label={'Give positive feedback'}
        >
          <ThumbsUp width={'16px'} />
        </button>
        <button
          onClick={handleFeedbackClick(false)}
          className={`${styles.feedbackButton} ${
            message.feedbackGiven === 'thumbs_down' ? styles.feedbackActive : ''
          }`}
          disabled={hasFeedback}
          title={'Give negative feedback'}
          aria-label={'Give negative feedback'}
        >
          <ThumbsDown width={'16px'} />
        </button>
      </div>
    </>
  );
};
